import React, { useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2 } from 'lucide-react';
import { Seo } from '@/components/common/Seo';
import { PageHero } from '@/components/common/PageHero';
import { OphirButton } from '@/components/common/OphirButton';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { submitContact } from '@/lib/api';
import { useContent } from '@/context/ContentContext';
import { SectionLabel } from '@/components/common/SectionLabel';
import { LocationsGrid } from '@/components/common/OfficeMap';

const labelCls = 'text-[12px] font-semibold uppercase tracking-[0.1em] text-ophir-navy';

export default function Contact() {
  const { content } = useContent();
  const seo = content?.seo?.contact;
  const contact = content?.contact || {};
  const types = content?.form_options?.inquiry_types || ['General Business Inquiry'];
  const [form, setForm] = useState({ name: '', email: '', company: '', telephone: '', inquiry_type: '', message: '', consent: false, company_website_hp: '' });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Please enter your name';
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) e.email = 'Enter a valid email';
    if (!form.inquiry_type) e.inquiry_type = 'Select an inquiry type';
    if (form.message.trim().length < 10) e.message = 'Please enter a longer message';
    if (!form.consent) e.consent = 'Consent is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await submitContact(form);
      setDone(true);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <>
        <Seo title={seo?.title} description={seo?.description} />
        <PageHero label="Contact" title="Thank you" />
        <section className="bg-white"><div className="container-ophir py-16">
          <div data-testid="contact-success" className="max-w-xl bg-ophir-ivory-soft border border-ophir-grey rounded-2xl p-8 shadow-ophir">
            <CheckCircle2 className="text-ophir-navy" size={40} />
            <h2 className="mt-4 font-serif text-[24px] text-ophir-navy-deep">Your message has been received</h2>
            <p className="mt-3 text-[15.5px] leading-relaxed text-ophir-muted">Thank you for contacting OPHIR SUPPLY. Our team will review your message and respond with clear next steps.</p>
            <div className="mt-6"><OphirButton to="/">Return Home</OphirButton></div>
          </div>
        </div></section>
      </>
    );
  }

  return (
    <>
      <Seo title={seo?.title} description={seo?.description} jsonLd={{ '@context': 'https://schema.org', '@type': 'ContactPage', name: 'Contact OPHIR SUPPLY' }} />
      <PageHero label="Contact" title="Contact our team" intro="Reach out regarding supplier opportunities, buyer inquiries or general business questions. We respond with clear, professional next steps." />

      <section className="bg-white">
        <div className="container-ophir py-16 lg:py-24">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-7">
              <form data-testid="contact-form" onSubmit={onSubmit} noValidate className="space-y-5">
                <input type="text" tabIndex={-1} autoComplete="off" className="hidden" value={form.company_website_hp} onChange={(e) => set('company_website_hp', e.target.value)} aria-hidden="true" />
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="c-name" className={labelCls}>Full name *</Label>
                    <Input id="c-name" data-testid="contact-name" value={form.name} onChange={(e) => set('name', e.target.value)} className="mt-2" />
                    {errors.name && <p className="mt-1 text-[13px] text-[#B42318]">{errors.name}</p>}
                  </div>
                  <div>
                    <Label htmlFor="c-email" className={labelCls}>Business email *</Label>
                    <Input id="c-email" type="email" data-testid="contact-email" value={form.email} onChange={(e) => set('email', e.target.value)} className="mt-2" />
                    {errors.email && <p className="mt-1 text-[13px] text-[#B42318]">{errors.email}</p>}
                  </div>
                  <div>
                    <Label htmlFor="c-company" className={labelCls}>Company</Label>
                    <Input id="c-company" data-testid="contact-company" value={form.company} onChange={(e) => set('company', e.target.value)} className="mt-2" />
                  </div>
                  <div>
                    <Label htmlFor="c-tel" className={labelCls}>Telephone</Label>
                    <Input id="c-tel" data-testid="contact-telephone" value={form.telephone} onChange={(e) => set('telephone', e.target.value)} className="mt-2" />
                  </div>
                </div>
                <div>
                  <Label className={labelCls}>Type of inquiry *</Label>
                  <Select value={form.inquiry_type} onValueChange={(v) => set('inquiry_type', v)}>
                    <SelectTrigger data-testid="contact-form-inquiry-type-select" className="mt-2"><SelectValue placeholder="Select an inquiry type" /></SelectTrigger>
                    <SelectContent>
                      {types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {errors.inquiry_type && <p className="mt-1 text-[13px] text-[#B42318]">{errors.inquiry_type}</p>}
                </div>
                <div>
                  <Label htmlFor="c-msg" className={labelCls}>Message *</Label>
                  <Textarea id="c-msg" data-testid="contact-message" rows={6} value={form.message} onChange={(e) => set('message', e.target.value)} className="mt-2" />
                  {errors.message && <p className="mt-1 text-[13px] text-[#B42318]">{errors.message}</p>}
                </div>
                <div className="flex items-start gap-3">
                  <Checkbox id="c-consent" data-testid="contact-consent" checked={form.consent} onCheckedChange={(v) => set('consent', !!v)} className="mt-1" />
                  <Label htmlFor="c-consent" className="text-[14px] font-normal leading-relaxed text-ophir-muted">I consent to OPHIR SUPPLY storing and using this information to respond to my inquiry. *</Label>
                </div>
                {errors.consent && <p className="text-[13px] text-[#B42318]">{errors.consent}</p>}
                <OphirButton type="submit" data-testid="contact-submit" disabled={submitting}>
                  {submitting ? 'Sending…' : 'Contact Our Team'}
                </OphirButton>
              </form>
            </div>
            <div className="lg:col-span-5">
              <div className="bg-ophir-ivory-soft border border-ophir-grey rounded-2xl overflow-hidden shadow-ophir">
                <img src="/images/ophir/contact/professional.webp" alt="An OPHIR SUPPLY professional reviewing documents and a product sample in a bright modern office" className="w-full h-64 object-cover" width={1408} height={768} />
                <div className="p-6">
                  <h3 className="font-serif text-[20px] text-ophir-navy-deep">Reliable Supply. Valuable Partnerships.</h3>
                  <p className="mt-3 text-[15px] leading-relaxed text-ophir-muted">
                    We review each inquiry carefully and respond with clear, professional next steps. Whether you are a supplier, a buyer or a partner, we look forward to hearing from you.
                  </p>
                  {(contact.email || contact.phone) && (
                    <ul className="mt-4 space-y-1.5 text-[15px] text-ophir-navy">
                      {contact.email && <li>{contact.email}</li>}
                      {contact.phone && <li>{contact.phone}</li>}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LOCATIONS */}
      <section className="bg-ophir-ivory-soft border-t border-ophir-grey">
        <div className="container-ophir py-16 lg:py-24">
          <div className="max-w-2xl mb-10">
            <SectionLabel>Our locations</SectionLabel>
            <h2 className="mt-5 font-serif text-[30px] sm:text-[36px] leading-tight text-ophir-navy-deep">Where we operate</h2>
            <p className="mt-4 text-[16.5px] leading-relaxed text-ophir-muted">Global Sourcing. North American Focus.</p>
          </div>
          <LocationsGrid locations={content?.locations || []} />
        </div>
      </section>
    </>
  );
}
